import { useState } from "react"
import { AlertTriangle, Loader2, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { PipelineGaps } from "../../api/apcm-ops.api"
import * as api from "../../api/apcm-ops.api"
import { useToast } from "@/components/ui/use-toast"

interface Props {
  orgId: string
  gaps: PipelineGaps | null
  onRefresh: () => void
  onViewPatients: (states: string[]) => void
  onOpenSetup: () => void
}

export function PipelineGapsBanner({
  orgId,
  gaps,
  onRefresh,
  onViewPatients,
  onOpenSetup,
}: Props) {
  const { toast } = useToast()
  const [initializing, setInitializing] = useState(false)

  if (!gaps) return null

  const totalStuck = Object.values(gaps.stuck_per_state).reduce(
    (sum, s) => sum + s.count,
    0,
  )
  const noStateCount = gaps.patients_without_state
  const showNothing = totalStuck === 0 && noStateCount === 0

  if (showNothing) return null

  const handleInitialize = async () => {
    setInitializing(true)
    try {
      await api.initializeAllPatients(orgId, false)
      toast({
        title: "Initialization started",
        description: "Patients without state are being placed into the pipeline.",
      })
      onRefresh()
    } catch (err: any) {
      toast({
        title: "Initialization failed",
        description: err?.response?.data?.detail || err?.message || "Unknown error",
        variant: "destructive",
      })
    } finally {
      setInitializing(false)
    }
  }

  return (
    <div className="space-y-2">
      {noStateCount > 0 && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-destructive">
              {noStateCount} patient{noStateCount === 1 ? "" : "s"} not in the pipeline
            </p>
            <p className="text-xs text-destructive/90 mt-0.5 leading-relaxed">
              These patients exist in the org but have no APCM state entry — they'll never
              appear in any stage until initialized. Click below to auto-assign starting
              states based on their consent / care-plan history.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 h-8 gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={initializing}
            onClick={handleInitialize}
          >
            {initializing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            Initialize {noStateCount}
          </Button>
        </div>
      )}

      {totalStuck > 0 && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-warning">
              {totalStuck} workflow{totalStuck === 1 ? "" : "s"} stuck in flight
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {Object.entries(gaps.stuck_per_state)
                .filter(([, s]) => s.count > 0)
                .map(([state, s]) => (
                  <button
                    key={state}
                    onClick={() => onViewPatients([state])}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-md border border-warning/30 bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning",
                      "hover:bg-warning/25 transition-colors",
                    )}
                  >
                    <span className="font-semibold">{s.count}</span>
                    <span>{humanState(state)}</span>
                    {s.oldest_age_hours !== null && s.oldest_age_hours > 0 && (
                      <span className="text-warning/80">
                        · oldest {humanAge(s.oldest_age_hours)}
                      </span>
                    )}
                  </button>
                ))}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 h-8 text-xs text-warning hover:bg-warning/10"
            onClick={onOpenSetup}
          >
            Run preflight →
          </Button>
        </div>
      )}
    </div>
  )
}

function humanState(state: string): string {
  return state
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function humanAge(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)}m`
  if (hours < 24) return `${Math.round(hours)}h`
  return `${Math.round(hours / 24)}d`
}
